import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { apiGet, apiPut } from '@/api/axios-client'
import { getErrorMessage } from '@/api/errors'

export type MusicProvider = 'youtube' | 'ytmusic' | 'spotify'
export interface Listening { userId: string; displayName: string; photoUrl: string | null; title: string; author: string; url: string; provider: MusicProvider; at: number }
export interface SearchHit { id: string; title: string; author: string; thumb: string | null; url: string }

export const musicKeys = { team: ['music', 'team'] as const, search: (q: string) => ['music', 'search', q] as const }

/** Everyone's current track, refreshed once a minute while the page is open. */
export function useListening() {
  return useQuery({
    queryKey: musicKeys.team,
    queryFn: () => apiGet<{ me: string; items: Listening[] }>('music/team'),
    refetchInterval: 60_000,
    staleTime: 30_000,
  })
}

export function useSearchMusic(q: string) {
  const term = q.trim()
  return useQuery({
    queryKey: musicKeys.search(term),
    queryFn: () => apiGet<{ configured: boolean; items: SearchHit[]; error?: string }>('music/search', { q: term }),
    enabled: term.length >= 2,
    staleTime: 5 * 60_000,
  })
}

export function useShare() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: { url: string | null; title?: string }) => apiPut<{ track: Listening | null }>('music/now', body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: musicKeys.team }),
    onError: (error) => toast.error(getErrorMessage(error)),
  })
}

/** The YouTube video id behind a link, when it has one; Spotify links do not play in the app. */
export function videoIdOf(url: string): string | null {
  try {
    const u = new URL(url)
    const id = u.searchParams.get('v')
    return id && /^[A-Za-z0-9_-]{6,20}$/.test(id) ? id : null
  } catch {
    return null
  }
}
